import React, { useState, useRef, useContext } from 'react';
import {
  Box,
  Avatar,
  Button,
  Typography,
  CircularProgress,
  IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { UserContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const MAX_SIZE = 5 * 1024 * 1024;
const ALLOWED = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export default function ProfilePhotoUpload() {
  const t = useAutoTranslation();

  const { user } = useContext(UserContext);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const inputRef = useRef();

  const hasCurrentPhoto = Boolean(user?.profilePhoto);
  const currentPhotoSrc = hasCurrentPhoto
    ? `/api/users/profile-photo?t=${Date.now()}`
    : null;

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      setError(t('Only JPEG, PNG, GIF, or WebP images are allowed'));
      return;
    }
    if (f.size > MAX_SIZE) {
      setError(t('Image must be under 5 MB'));
      return;
    }
    setError('');
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('photo', file);
    try {
      await axios.post(APICONSTANTS.uploadProfilePhoto, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      window.location.reload();
    } catch (err) {
      setError(err?.response?.data?.error || t('Upload failed'));
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    setError('');
    try {
      await axios.delete(APICONSTANTS.deleteProfilePhoto);
      window.location.reload();
    } catch (err) {
      setError(t('Failed to remove photo'));
      setRemoving(false);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setPreview(null);
    setError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const displaySrc = preview || currentPhotoSrc;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        py: 1
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <Avatar src={displaySrc} sx={{ width: 96, height: 96, fontSize: 36 }}>
          {!displaySrc && (user?.firstName?.[0]?.toUpperCase() || '?')}
        </Avatar>
        {(hasCurrentPhoto || preview) && !preview && (
          <IconButton
            size="small"
            onClick={handleRemove}
            disabled={removing}
            sx={{
              position: 'absolute',
              bottom: -4,
              right: -4,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { bgcolor: 'error.lighter' }
            }}
          >
            {removing ? (
              <CircularProgress size={14} />
            ) : (
              <DeleteIcon fontSize="small" color="error" />
            )}
          </IconButton>
        )}
      </Box>

      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}

      <input
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        ref={inputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {!preview ? (
        <Button
          variant="outlined"
          size="small"
          onClick={() => inputRef.current?.click()}
        >
          {hasCurrentPhoto ? t('Change photo') : t('Upload photo')}
        </Button>
      ) : (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleUpload}
            disabled={uploading}
            startIcon={
              uploading ? <CircularProgress size={14} color="inherit" /> : null
            }
          >
            {uploading ? t('Saving...') : t('Save')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCancel}
            disabled={uploading}
          >
            {t('Cancel')}
          </Button>
        </Box>
      )}

      {hasCurrentPhoto && !preview && (
        <Button
          variant="text"
          size="small"
          color="error"
          onClick={handleRemove}
          disabled={removing}
          startIcon={
            removing ? <CircularProgress size={14} color="inherit" /> : null
          }
        >
          {removing ? t('Removing...') : t('Remove photo')}
        </Button>
      )}
    </Box>
  );
}
